import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { CardBody } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Btn } from '../../AbstractElements';
import { useGetFeedbacksQuery, useLazyGetFeedbacksQuery } from '../../features/feedbackApiSlice';
import { toast } from 'react-toastify';

const SubscriptionFeedback = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: feedbacks,
    isLoading,
    isSuccess,
    refetch,
  } = useGetFeedbacksQuery({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchTerm,
    form_name: 'subscription_updates',
  });

  const [getAllFeedbacks] = useLazyGetFeedbacksQuery();

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    if (isSuccess && feedbacks) {
      const feedbacksList = feedbacks?.data || [];
      setData(Array.isArray(feedbacksList) ? feedbacksList : []);
      setTotalRows(feedbacks?.pagination?.total || 0);
    }
  }, [feedbacks, isSuccess]);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const convertJSONToCSV = (jsonData, columnHeaders) => {
    if (jsonData.length === 0) {
      return '';
    }
    const headers = columnHeaders.join(',') + '\n';
    const rows = jsonData
      .map((row) => {
        return columnHeaders
          .map((field) => {
            const value = row[field] || '';
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(',');
      })
      .join('\n');
    return headers + rows;
  };

  const downloadFeedbackCSV = async () => {
    try {
      setIsDownloadingCsv(true);
      const loadingToast = toast.loading('Fetching all feedbacks...');

      const result = await getAllFeedbacks({
        page: 1,
        limit: 10000,
        search: '',
        form_name: 'subscription_updates',
      }).unwrap();

      const allFeedbacks = result?.data || [];

      if (allFeedbacks.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('No feedbacks to download');
        setIsDownloadingCsv(false);
        return;
      }

      const csvHeaders = ['id', 'user_id', 'email', 'reason', 'tags', 'text', 'form_name', 'created_at'];
      const csvData = allFeedbacks.map((feedback) => ({
        id: feedback.id || '',
        user_id: feedback.user_id || '',
        email: feedback.email || '',
        reason: feedback.reason || '',
        tags: Array.isArray(feedback.tags) ? feedback.tags.join('; ') : (feedback.tags || ''),
        text: feedback.text || '',
        form_name: feedback.form_name || '',
        created_at: feedback.created_at ? new Date(feedback.created_at).toLocaleString() : '',
      }));

      const csvContent = convertJSONToCSV(csvData, csvHeaders);

      if (csvContent === '') {
        toast.dismiss(loadingToast);
        toast.error('No data to download');
        setIsDownloadingCsv(false);
        return;
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `SubscriptionFeedbacks_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded ${allFeedbacks.length} feedback(s)`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Error generating CSV. Please try again.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const tableColumns = [
    {
      name: 'Id',
      selector: (row) => `${row?.id || ''}`,
      width: '70px',
      sortable: true,
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'User ID',
      selector: (row) => `${row?.user_id || ''}`,
      sortable: true,
      center: false,
      width: '100px',
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'Email',
      selector: (row) => `${row?.email || ''}`,
      sortable: true,
      center: false,
      width: '250px',
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'TYPE',
      center: false,
      cell: (row) => (
        <div style={{ textAlign: 'left', width: '100%' }}>
          <span
            className="badge rounded-pill px-2 py-1"
            style={{
              backgroundColor:
                row?.type === 'cancelled'
                  ? '#fbe4e6'
                  : row?.type === 'paused'
                    ? '#f9ebcf'
                    : row?.type === 'formula review'
                      ? '#dceaf8'
                      : row?.type === 'coupon applied'
                        ? '#dcefd4'
                        : '#e9ecef',
              color:
                row?.type === 'cancelled'
                  ? '#b4232b'
                  : row?.type === 'paused'
                    ? '#9a5c00'
                    : row?.type === 'formula review'
                      ? '#1b6db8'
                      : row?.type === 'coupon applied'
                        ? '#3f7f1a'
                        : '#495057',
              fontWeight: 400,
              textTransform: 'capitalize',
            }}
          >
            {row?.type || '-'}
          </span>
        </div>
      ),
      width: '180px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'NOTE',
      selector: (row) => row?.text || '-',
      sortable: false,
      center: false,
      wrap: true,
      width: '340px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'REASON',
      selector: (row) => row?.reason || '—',
      sortable: false,
      center: false,
      wrap: true,
      width: '160px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'TAGS',
      selector: (row) => row?.tags || '—',
      sortable: false,
      center: false,
      wrap: true,
      width: '240px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'Created At',
      selector: (row) => (row?.created_at ? new Date(row.created_at) : ''),
      sortable: true,
      center: false,
      cell: (row) => (row?.created_at ? new Date(row.created_at).toLocaleString() : ''),
      style: { justifyContent: 'flex-start' },
    },
  ];

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <h1 className="t-text-xl t-font-bold t-mb-2 t-ml-2 md:t-ml-3 t-text-secondary">
        Subscription Feedback
      </h1>
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="mb-3">
              <div className="col-8">
                <input
                  type="text"
                  placeholder="Search feedbacks..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control"
                />
              </div>
              <div className="col-4 text-end">
                <Btn
                  attrBtn={{
                    color: 'primary',
                    disabled: isDownloadingCsv || isLoading,
                    onClick: downloadFeedbackCSV,
                  }}
                >
                  {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
                </Btn>
              </div>
            </Row>

            {isLoading && (
              <div className="text-center py-3 mt-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading feedbacks...</p>
              </div>
            )}

            {!isLoading && (
              <div className="mb-3 text-muted mt-3">
                Showing {data.length} of {totalRows} feedbacks
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
            )}

            <DataTable
              columns={tableColumns}
              data={data}
              striped={true}
              center={false}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={isLoading}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                noRowsPerPage: false,
              }}
            />
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default SubscriptionFeedback;
